'use client'

import * as React from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { cn } from '../../../lib/utils'
import { Icon, type iconTypes } from '../Icon'
import type {
  NavigationEditorItem,
  NavigationLayoutChange,
  RestructurableNavigationLayoutProps,
} from './navigationEditorTypes'
import {
  canCreateNestUnderLink,
  findEditorItemById,
  getExistingNestGroupIdForLinkAnchor,
  shouldCreateNewNestGroupUnderLink,
} from './navigationNestUtils'

export type {
  NavigationEditorItem,
  NavigationLayoutChange,
  RestructurableNavigationLayoutProps,
} from './navigationEditorTypes'
export {
  canCreateNestUnderLink,
  findEditorItemById,
  getExistingNestGroupIdForLinkAnchor,
  shouldCreateNewNestGroupUnderLink,
} from './navigationNestUtils'

export const NAV_GROUP_DRAG_TYPE = 'NAV_GROUP'
export const NAV_LINK_DRAG_TYPE = 'NAV_LINK'
export const NAV_SECTION_DRAG_TYPE = 'NAV_SECTION'

export const NAV_DRAG_TYPES = [
  NAV_GROUP_DRAG_TYPE,
  NAV_LINK_DRAG_TYPE,
  NAV_SECTION_DRAG_TYPE,
]

export function flattenEditorTree(
  items: NavigationEditorItem[],
  parentId: string | null = null,
): NavigationLayoutChange[] {
  const result: NavigationLayoutChange[] = []

  items.forEach((item, index) => {
    result.push({
      id: item.id,
      parentId,
      sortIndex: index,
    })

    if (item.children?.length) {
      result.push(...flattenEditorTree(item.children, item.id))
    }
  })

  return result
}

function removeItemFromTree(
  items: NavigationEditorItem[],
  dragId: string,
): { tree: NavigationEditorItem[]; dragged: NavigationEditorItem | null } {
  for (let index = 0; index < items.length; index++) {
    const item = items[index]
    if (!item) continue
    if (item.id === dragId) {
      return {
        tree: [...items.slice(0, index), ...items.slice(index + 1)],
        dragged: item,
      }
    }

    if (item.children?.length) {
      const result = removeItemFromTree(item.children, dragId)
      if (result.dragged) {
        return {
          tree: items.map((node, nodeIndex) =>
            nodeIndex === index ? { ...node, children: result.tree } : node,
          ),
          dragged: result.dragged,
        }
      }
    }
  }

  return { tree: items, dragged: null }
}

function insertItemInTree(
  items: NavigationEditorItem[],
  targetParentId: string | null,
  targetIndex: number,
  dragged: NavigationEditorItem,
): NavigationEditorItem[] {
  if (targetParentId === null) {
    const next = [...items]
    next.splice(targetIndex, 0, dragged)
    return next
  }

  return items.map((item) => {
    if (item.id === targetParentId) {
      const children = [...(item.children ?? [])]
      children.splice(targetIndex, 0, dragged)
      return { ...item, children }
    }

    if (item.children?.length) {
      return {
        ...item,
        children: insertItemInTree(
          item.children,
          targetParentId,
          targetIndex,
          dragged,
        ),
      }
    }

    return item
  })
}

function findItemParentAndIndex(
  items: NavigationEditorItem[],
  id: string,
  parentId: string | null = null,
): { parentId: string | null; index: number } | null {
  for (let index = 0; index < items.length; index++) {
    const item = items[index]
    if (!item) continue
    if (item.id === id) {
      return { parentId, index }
    }

    if (item.children?.length) {
      const found = findItemParentAndIndex(item.children, id, item.id)
      if (found) return found
    }
  }

  return null
}

export function moveItemInTree(
  items: NavigationEditorItem[],
  dragId: string,
  targetParentId: string | null,
  targetIndex: number,
): NavigationEditorItem[] {
  if (!canMoveNavigationItem(items, dragId, targetParentId)) {
    return items
  }

  const dragLocation = findItemParentAndIndex(items, dragId)
  if (!dragLocation) return items

  const { tree, dragged } = removeItemFromTree(items, dragId)
  if (!dragged) return items

  let adjustedTargetIndex = targetIndex
  if (
    dragLocation.parentId === targetParentId &&
    dragLocation.index < targetIndex
  ) {
    adjustedTargetIndex = targetIndex - 1
  }

  return insertItemInTree(tree, targetParentId, adjustedTargetIndex, dragged)
}

function slugifyNavigationLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function createNestGroupInTree(
  items: NavigationEditorItem[],
  anchorId: string,
  dragId: string,
  groupId: string,
): NavigationEditorItem[] {
  if (!shouldCreateNewNestGroupUnderLink(items, anchorId, dragId)) {
    return items
  }

  const anchorLocation = findItemParentAndIndex(items, anchorId)
  const dragLocation = findItemParentAndIndex(items, dragId)
  const anchor = findEditorItemById(items, anchorId)
  const dragged = findEditorItemById(items, dragId)
  if (!anchorLocation || !dragLocation || !anchor || !dragged) {
    return items
  }

  const parentId = anchorLocation.parentId
  if (!parentId) return items

  let insertIndex = anchorLocation.index
  if (dragLocation.parentId === parentId && dragLocation.index < insertIndex) {
    insertIndex -= 1
  }

  const { tree: treeWithoutDrag, dragged: removedDragged } = removeItemFromTree(
    items,
    dragId,
  )
  if (!removedDragged) return items

  const { tree: treeWithoutAnchor, dragged: removedAnchor } =
    removeItemFromTree(treeWithoutDrag, anchorId)
  if (!removedAnchor) return items

  const group: NavigationEditorItem = {
    id: groupId,
    url: `_group/${slugifyNavigationLabel(removedAnchor.label)}`,
    label: removedAnchor.label,
    icon: removedAnchor.icon,
    sortIndex: insertIndex,
    parentId,
    kind: 'group',
    children: [
      { ...removedAnchor, parentId: groupId, sortIndex: 0 },
      { ...removedDragged, parentId: groupId, sortIndex: 1 },
    ],
  }

  return insertItemInTree(treeWithoutAnchor, parentId, insertIndex, group)
}

function isGroupEditorItem(item: NavigationEditorItem): boolean {
  return item.kind === 'group' || item.url.startsWith('_group/')
}

function isSectionEditorItem(item: NavigationEditorItem): boolean {
  return item.kind === 'section' || item.url.startsWith('_section/')
}

export function pruneEmptyGroupsFromTree(
  items: NavigationEditorItem[],
): NavigationEditorItem[] {
  return pruneEmptyNavigationFromTree(items)
}

export function pruneEmptyNavigationFromTree(
  items: NavigationEditorItem[],
  isRootLevel = true,
): NavigationEditorItem[] {
  const pruned: NavigationEditorItem[] = []

  for (const [index, item] of items.entries()) {
    const children = item.children?.length
      ? pruneEmptyNavigationFromTree(item.children, false)
      : []

    if (isGroupEditorItem(item)) {
      if (!children.length) {
        continue
      }
      pruned.push({ ...item, children })
      continue
    }

    if (isSectionEditorItem(item) && isRootLevel) {
      const isMyWorkspaceSection = index === 0
      if (!isMyWorkspaceSection && !children.length) {
        continue
      }
      pruned.push({ ...item, children })
      continue
    }

    if (item.children?.length || children.length) {
      pruned.push({
        ...item,
        children: children.length ? children : undefined,
      })
      continue
    }

    pruned.push(item)
  }

  return pruned
}

function isEditorItemDescendant(
  items: NavigationEditorItem[],
  ancestorId: string,
  candidateId: string,
): boolean {
  const ancestor = findEditorItemById(items, ancestorId)
  if (!ancestor?.children?.length) return false

  const walk = (nodes: NavigationEditorItem[]): boolean => {
    for (const node of nodes) {
      if (node.id === candidateId) return true
      if (node.children?.length && walk(node.children)) return true
    }
    return false
  }

  return walk(ancestor.children)
}

function getPersonalRootSectionId(
  items: NavigationEditorItem[],
): string | null {
  const personalRoot = items.find(
    (item) => item.navScope === 'personal' && item.kind === 'section',
  )
  return personalRoot?.id ?? null
}

export function isDropTargetInPersonalScope(
  items: NavigationEditorItem[],
  targetParentId: string | null,
): boolean {
  const personalRootId = getPersonalRootSectionId(items)
  if (!personalRootId) {
    return false
  }

  if (targetParentId === personalRootId) {
    return true
  }

  if (!targetParentId) {
    return false
  }

  return isEditorItemDescendant(items, personalRootId, targetParentId)
}

export function canMoveNavigationItem(
  items: NavigationEditorItem[],
  dragId: string,
  targetParentId: string | null,
): boolean {
  if (targetParentId === dragId) return false

  const dragged = findEditorItemById(items, dragId)
  if (!dragged) return false

  if (targetParentId && isEditorItemDescendant(items, dragId, targetParentId)) {
    return false
  }

  const personalRootId = getPersonalRootSectionId(items)

  if (
    dragged.navScope === 'personal' &&
    dragged.kind === 'section' &&
    dragged.url.startsWith('_section/')
  ) {
    return false
  }

  if (dragged.kind === 'section') {
    return targetParentId === null && dragged.navScope === 'team'
  }

  if (targetParentId === null) {
    return false
  }

  const targetParent = findEditorItemById(items, targetParentId)
  if (!targetParent) return false

  const droppingIntoPersonal = isDropTargetInPersonalScope(
    items,
    targetParentId,
  )

  if (droppingIntoPersonal) {
    if (dragged.navScope === 'team' && dragged.kind !== 'link') {
      return false
    }
    return dragged.kind === 'link' || dragged.kind === 'group'
  }

  if (targetParent.kind === 'link') {
    return false
  }

  if (targetParent.kind === 'section') {
    return dragged.kind === 'link' || dragged.kind === 'group'
  }

  if (targetParent.kind === 'group') {
    return dragged.kind === 'link' || dragged.kind === 'group'
  }

  return false
}

const NavDropZone = ({
  parentId,
  targetIndex,
  onDropItem,
  enabled,
}: {
  parentId: string | null
  targetIndex: number
  onDropItem: (dragId: string, parentId: string | null, index: number) => void
  enabled: boolean
}) => {
  const [{ isOver }, drop] = useDrop({
    accept: NAV_DRAG_TYPES,
    canDrop: () => enabled,
    drop: (item: { id: string }) => {
      onDropItem(item.id, parentId, targetIndex)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  })

  if (!enabled) return null

  return (
    <div
      ref={drop as unknown as React.Ref<HTMLDivElement>}
      className={cn('h-2 rounded-full transition-all', {
        'bg-primary/40 h-3': isOver,
        'bg-transparent': !isOver,
      })}
    />
  )
}

const DraggableNavRow = ({
  item,
  depth,
  enabled,
  onMove,
}: {
  item: NavigationEditorItem
  depth: number
  enabled: boolean
  onMove: (dragId: string, parentId: string | null, index: number) => void
}) => {
  const dragType =
    item.kind === 'link'
      ? NAV_LINK_DRAG_TYPE
      : item.kind === 'section'
        ? NAV_SECTION_DRAG_TYPE
        : NAV_GROUP_DRAG_TYPE

  const [{ isDragging }, drag] = useDrag({
    type: dragType,
    item: { id: item.id },
    canDrag: () => enabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  return (
    <div
      ref={drag as unknown as React.Ref<HTMLDivElement>}
      className={cn('rounded-md border bg-background px-3 py-2', {
        'opacity-50': isDragging,
      })}
      style={{ marginLeft: depth * 12 }}
    >
      <div className="flex items-center gap-2 text-sm">
        {enabled ? (
          <Icon name="GripVertical" className="text-muted-foreground" />
        ) : null}
        {item.icon ? (
          <Icon
            name={item.icon as iconTypes}
            className="text-muted-foreground"
          />
        ) : null}
        <span className="font-medium">{item.label}</span>
        {item.pagePath ? (
          <span className="text-xs text-muted-foreground">
            /{item.pagePath}
          </span>
        ) : null}
      </div>
      {item.children?.length ? (
        <NavigationEditorList
          items={item.children}
          parentId={item.id}
          depth={depth + 1}
          enabled={enabled}
          onMove={onMove}
        />
      ) : null}
    </div>
  )
}

const NavigationEditorList = ({
  items,
  parentId,
  depth,
  enabled,
  onMove,
}: {
  items: NavigationEditorItem[]
  parentId: string | null
  depth: number
  enabled: boolean
  onMove: (dragId: string, parentId: string | null, index: number) => void
}) => (
  <div className="mt-2 space-y-2">
    <NavDropZone
      parentId={parentId}
      targetIndex={0}
      onDropItem={onMove}
      enabled={enabled}
    />
    {items.map((item, index) => (
      <React.Fragment key={item.id}>
        <DraggableNavRow
          item={item}
          depth={depth}
          enabled={enabled}
          onMove={onMove}
        />
        <NavDropZone
          parentId={parentId}
          targetIndex={index + 1}
          onDropItem={onMove}
          enabled={enabled}
        />
      </React.Fragment>
    ))}
  </div>
)

export const RestructurableNavigationLayout = ({
  items,
  enableDragAndDrop = true,
  onNavigationLayoutChange,
  className,
}: RestructurableNavigationLayoutProps) => {
  const [tree, setTree] = React.useState(items)

  React.useEffect(() => {
    setTree(items)
  }, [items])

  const handleMove = React.useCallback(
    (dragId: string, targetParentId: string | null, targetIndex: number) => {
      setTree((current) => {
        const next = moveItemInTree(
          current,
          dragId,
          targetParentId,
          targetIndex,
        )
        const pruned = pruneEmptyGroupsFromTree(next)
        onNavigationLayoutChange?.(flattenEditorTree(pruned))
        return pruned
      })
    },
    [onNavigationLayoutChange],
  )

  return (
    <div className={cn('space-y-3', className)}>
      <NavigationEditorList
        items={tree}
        parentId={null}
        depth={0}
        enabled={enableDragAndDrop}
        onMove={handleMove}
      />
    </div>
  )
}

export function mapNavigationItemsToEditorTree(
  items: Array<{
    id: string
    url: string
    label?: string | null
    icon?: string | null
    sortIndex: number
    parentId?: string | null
    page?: { path: string; id?: string; memberId?: string | null } | null
    children?: Array<{
      id: string
      url: string
      label?: string | null
      icon?: string | null
      sortIndex: number
      parentId?: string | null
      page?: { path: string; id?: string; memberId?: string | null } | null
      children?: unknown[]
    }>
  }>,
): NavigationEditorItem[] {
  return items.map((item) => {
    const kind: NavigationEditorItem['kind'] = item.url.startsWith('_section/')
      ? 'section'
      : item.url.startsWith('_group/')
        ? 'group'
        : 'link'

    return {
      id: item.id,
      url: item.url,
      label: item.label || item.page?.path || item.url,
      icon: item.icon,
      sortIndex: item.sortIndex,
      parentId: item.parentId ?? null,
      pagePath: item.page?.path ?? null,
      pageId: item.page?.id ?? null,
      pageIsPrivate:
        item.page?.memberId !== undefined && item.page.memberId !== null,
      kind,
      children: item.children
        ? mapNavigationItemsToEditorTree(
            item.children as Parameters<
              typeof mapNavigationItemsToEditorTree
            >[0],
          )
        : undefined,
    }
  })
}
